import { useState, useMemo } from 'react';
import api from '../../../services/api';
import './PasswordChangeForm.css';

interface PasswordChangeFormProps {
  onSuccess?: () => void;
  hasPassword?: boolean;  // Whether user has a password set (false for LINE-only users)
}

interface PasswordFormState {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordStrength {
  checks: {
    length: boolean;
    lowercase: boolean;
    uppercase: boolean;
    digit: boolean;
  };
  percentage: number;
}

// Password strength helper functions (outside component to avoid recreation)
const getPasswordStrength = (password: string): PasswordStrength => {
  if (!password) return { checks: { length: false, lowercase: false, uppercase: false, digit: false }, percentage: 0 };

  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    digit: /[0-9]/.test(password),
  };

  const passed = Object.values(checks).filter(Boolean).length;
  const percentage = (passed / 4) * 100;

  return { checks, percentage };
};

const getPasswordStrengthColor = (password: string): string => {
  const { percentage } = getPasswordStrength(password);
  if (percentage === 0) return '#e5e7eb';
  if (percentage <= 25) return '#ef4444';
  if (percentage <= 50) return '#f97316';
  if (percentage <= 75) return '#eab308';
  return '#10b981';
};

const PasswordChangeForm = ({ onSuccess, hasPassword = true }: PasswordChangeFormProps) => {
  const [passwordForm, setPasswordForm] = useState<PasswordFormState>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Memoize password strength calculation
  const strength = useMemo(() => getPasswordStrength(passwordForm.newPassword), [passwordForm.newPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    // Validate password requirements: 8+ chars, uppercase, lowercase, digit
    const hasLowercase = /[a-z]/.test(passwordForm.newPassword);
    const hasUppercase = /[A-Z]/.test(passwordForm.newPassword);
    const hasDigit = /[0-9]/.test(passwordForm.newPassword);

    if (passwordForm.newPassword.length < 8 || !hasLowercase || !hasUppercase || !hasDigit) {
      setMessage({ type: 'error', text: '新密碼至少需要 8 個字元，包含大小寫字母和數字' });
      return;
    }

    // Validate confirm password
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: 'error', text: '新密碼與確認密碼不符' });
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/api/members/me/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
      });

      if (response.success) {
        setMessage({ type: 'success', text: '密碼變更成功。建議您重新登入以確保安全。' });
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        onSuccess?.();
      } else {
        setMessage({ type: 'error', text: response.error?.message || '密碼變更失敗，請檢查您的輸入' });
      }
    } catch {
      setMessage({ type: 'error', text: '網路錯誤，請稍後再試' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="password-change-form">

      {message && (
        <div className={`password-change-form__alert password-change-form__alert--${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="password-change-form__form">
        {hasPassword && (
          <div className="password-change-form__form-group">
            <label htmlFor="currentPassword">目前密碼 *</label>
            <input
              id="currentPassword"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              required={hasPassword}
            />
          </div>
        )}

        <div className="password-change-form__form-group">
          <label htmlFor="newPassword">新密碼 *</label>
          <input
            id="newPassword"
            type="password"
            value={passwordForm.newPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
            required
            minLength={8}
          />
          {passwordForm.newPassword && (
            <div className="password-change-form__strength">
              <div className="password-change-form__strength-bar">
                <div
                  className="password-change-form__strength-fill"
                  style={{
                    width: `${strength.percentage}%`,
                    backgroundColor: getPasswordStrengthColor(passwordForm.newPassword)
                  }}
                />
              </div>
              <ul className="password-change-form__requirements">
                <li className={strength.checks.length ? 'valid' : 'invalid'}>
                  ✓ 至少 8 個字元
                </li>
                <li className={strength.checks.lowercase ? 'valid' : 'invalid'}>
                  ✓ 小寫字母
                </li>
                <li className={strength.checks.uppercase ? 'valid' : 'invalid'}>
                  ✓ 大寫字母
                </li>
                <li className={strength.checks.digit ? 'valid' : 'invalid'}>
                  ✓ 數字
                </li>
              </ul>
            </div>
          )}
          <small>至少需要 8 個字元，包含大小寫字母和數字</small>
        </div>

        <div className="password-change-form__form-group">
          <label htmlFor="confirmPassword">確認新密碼 *</label>
          <input
            id="confirmPassword"
            type="password"
            value={passwordForm.confirmPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
            required
            minLength={8}
          />
        </div>

        <button type="submit" className="password-change-form__submit" disabled={isLoading}>
          {isLoading ? (hasPassword ? '變更中...' : '設定中...') : (hasPassword ? '變更密碼' : '設定密碼')}
        </button>
      </form>
    </div>
  );
};

export default PasswordChangeForm;
